import React, { useState } from 'react';
import { MapPin, Briefcase, DollarSign, Users, ArrowRight, Search, CheckCircle, Heart, Globe, Send, ChevronLeft } from 'lucide-react';

export default function CareersPage() {
  const [currentView, setCurrentView] = useState('careers-main');
  const [selectedJob, setSelectedJob] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [applicationSuccess, setApplicationSuccess] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    linkedIn: '',
    portfolio: '',
    coverLetter: ''
  });

  const jobListings = [
    {
      id: 1,
      title: 'React Frontend Developer',
      location: 'India / Remote',
      type: 'Full-time',
      salary: '₹50,000 - ₹70,000',
      image: '⚛️',
      department: 'Engineering',
      experience: '3-5 years',
      shortDesc: 'Build beautiful, responsive interfaces for our IEEE paper platform',
      skills: ['React.js', 'JavaScript', 'TypeScript', 'Redux', 'HTML/CSS', 'Git', 'REST APIs'],
      benefits: ['Health Insurance', 'Remote Work', 'Professional Development', 'Competitive Salary']
    },
    {
      id: 2,
      title: 'IoT Architect Engineer',
      location: 'India / Remote',
      type: 'Full-time',
      salary: '₹80,000 - ₹120,000',
      image: '🔌',
      department: 'Engineering',
      experience: '5-8 years',
      shortDesc: 'Design and architect IoT solutions for connected research devices',
      skills: ['IoT Architecture', 'Embedded C/C++', 'Python', 'MQTT', 'Arduino', 'Cloud Platforms', 'Microcontrollers'],
      benefits: ['Competitive Salary', 'Remote Work', 'Learning Opportunities', 'Innovation Environment']
    },
    {
      id: 3,
      title: 'Full Stack Engineer',
      location: 'India / Remote',
      type: 'Full-time',
      salary: '₹60,000 - ₹85,000',
      image: '🔗',
      department: 'Engineering',
      experience: '4-6 years',
      shortDesc: 'Build end-to-end solutions combining frontend and backend technologies',
      skills: ['React', 'Node.js', 'MongoDB', 'PostgreSQL', 'REST APIs', 'Docker', 'AWS'],
      benefits: ['Health Insurance', 'Remote Work', 'Technology Budget', 'Flexible Hours']
    }
  ];

  const filteredJobs = jobListings.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         job.department.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLocation = selectedLocation === 'all' || job.location.includes(selectedLocation);
    return matchesSearch && matchesLocation;
  });

  const locations = ['all', 'India', 'Remote','Hybrid'];

  const AnimatedBackground = () => (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-blue-950 to-emerald-950" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" />
      <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-teal-500/20 rounded-full blur-3xl animate-pulse" />
    </div>
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplicationSubmit = () => {
    if (formData.fullName && formData.email && formData.coverLetter) {
      setApplicationSuccess(true);
      setTimeout(() => {
        setApplicationSuccess(false);
        setSelectedJob(null);
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          linkedIn: '',
          portfolio: '',
          coverLetter: ''
        });
        setCurrentView('careers-main');
      }, 3000);
    }
  };

  if (currentView === 'job-detail' && selectedJob) {
    return (
      <div className="relative min-h-screen bg-black overflow-hidden">
        <AnimatedBackground />
        
        <nav className="relative z-40 sticky top-0 bg-black/80 backdrop-blur-xl border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              <div className="text-white font-bold text-2xl">Careers</div>
              <button
                onClick={() => setCurrentView('careers-main')}
                className="flex items-center gap-2 text-white/90 hover:text-white transition text-sm font-medium"
              >
                <ChevronLeft className="w-5 h-5" />
                Back to Jobs
              </button>
            </div>
          </div>
        </nav>

        <section className="relative py-16 px-4">
          <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-12 mb-8">
                <div className="text-6xl mb-6">{selectedJob.image}</div>
                
                <div className="mb-8">
                  <h1 className="text-5xl font-bold text-white mb-4">{selectedJob.title}</h1>
                  <div className="flex flex-wrap gap-4 text-white/70">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-5 h-5" />
                      <span>{selectedJob.location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-5 h-5" />
                      <span>{selectedJob.type}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      
                      <span>{selectedJob.salary}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold text-blue-400 mb-4">About the Role</h3>
                    <p className="text-white/80">Join our dynamic team and help revolutionize academic publishing. You'll be working on cutting-edge technology that impacts researchers worldwide.</p>
                  </div>

                  <div>
                    <h3 className="text-2xl font-bold text-blue-400 mb-4">Responsibilities</h3>
                    <ul className="list-disc list-inside space-y-2 text-white/80">
                      <li>Develop and maintain high-quality code</li>
                      <li>Collaborate with cross-functional teams</li>
                      <li>Participate in code reviews and best practices</li>
                      <li>Contribute to product innovation and improvement</li>
                      <li>Mentor junior team members</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-2xl font-bold text-blue-400 mb-4">Requirements</h3>
                    <ul className="list-disc list-inside space-y-2 text-white/80">
                      <li>{selectedJob.experience} of professional experience</li>
                      <li>Strong technical skills in relevant technologies</li>
                      <li>Problem-solving and analytical abilities</li>
                      <li>Excellent communication skills</li>
                      <li>Passion for innovation</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8">
                  <h3 className="text-2xl font-bold text-white mb-6">Key Skills</h3>
                  <div className="flex flex-wrap gap-3">
                    {selectedJob.skills.map((skill, idx) => (
                      <span key={idx} className="bg-gradient-to-r from-blue-500/20 to-emerald-500/20 border border-blue-500/20 text-white/80 px-4 py-2 rounded-lg text-sm">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8">
                  <h3 className="text-2xl font-bold text-white mb-6">Benefits</h3>
                  <div className="space-y-3">
                    {selectedJob.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                        <span className="text-white/80">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="md:col-span-1">
              <div className="bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-white/10 rounded-3xl p-8 sticky top-32">
                <h3 className="text-2xl font-bold text-white mb-6">Apply Now</h3>
                
                {applicationSuccess ? (
                  <div className="text-center py-12">
                    <div className="text-5xl mb-4">✅</div>
                    <h4 className="text-2xl font-bold text-green-400 mb-2">Application Sent!</h4>
                    <p className="text-white/70">Thank you for your interest. We'll review your application and contact you soon.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">Full Name *</label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-white/10 border border-white/10 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Your name"
                      />
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-white/10 border border-white/10 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="your@email.com"
                      />
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-white/10 border border-white/10 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="+91 976 000-0000"
                      />
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">LinkedIn Profile</label>
                      <input
                        type="url"
                        name="linkedIn"
                        value={formData.linkedIn}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-white/10 border border-white/10 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="linkedin.com/in/yourprofile"
                      />
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">Portfolio/GitHub</label>
                      <input
                        type="url"
                        name="portfolio"
                        value={formData.portfolio}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-white/10 border border-white/10 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="github.com/yourprofile"
                      />
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">Cover Letter *</label>
                      <textarea
                        name="coverLetter"
                        value={formData.coverLetter}
                        onChange={handleInputChange}
                        rows="5"
                        className="w-full px-4 py-2 bg-white/10 border border-white/10 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                        placeholder="Tell us why you're interested in this role..."
                      />
                    </div>

                    <button
                      onClick={handleApplicationSubmit}
                      className="w-full bg-gradient-to-r from-blue-600 to-emerald-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition flex items-center justify-center gap-2"
                    >
                      <Send className="w-5 h-5" />
                      Submit Application
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-black overflow-hidden">
      <AnimatedBackground />

      <nav className="relative z-40 sticky top-0 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="text-white font-bold text-2xl bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent">
              FreeIeee Careers
            </div>
            <button
              onClick={() => window.history.back()}
              className="text-white/90 hover:text-white transition text-sm font-medium"
            >
              Back
            </button>
          </div>
        </div>
      </nav>

      <section className="relative pt-20 pb-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-6xl md:text-7xl font-black text-white mb-6">Join Our Team</h1>
          <p className="text-xl text-white/70 max-w-3xl mx-auto">
            Be part of a global movement revolutionizing academic publishing. We're building the future of research technology.
          </p>
        </div>
      </section>

      <section className="relative py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {[
              {
                icon: Heart,
                title: 'Mission-Driven',
                desc: 'Work on technology that impacts millions of researchers globally'
              },
              {
                icon: Users,
                title: 'Collaborative',
                desc: 'Join a diverse team that values innovation and teamwork'
              },
              {
                icon: Globe,
                title: 'Global',
                desc: 'Work with talented people across Poland, India, and beyond'
              }
            ].map((item, idx) => (
              <div key={idx} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition">
                <item.icon className="w-12 h-12 text-blue-400 mb-4" />
                <h3 className="text-2xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-white/70">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 mb-12">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/60 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by job title or department..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-6 py-3 bg-white/10 border border-white/10 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                />
              </div>

              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/60 w-5 h-5" />
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="w-full pl-12 pr-6 py-3 bg-white/10 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition appearance-none cursor-pointer"
                >
                  {locations.map((loc) => (
                    <option key={loc} value={loc} className="bg-slate-900">
                      {loc === 'all' ? 'All Locations' : loc}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {filteredJobs.length > 0 ? (
              filteredJobs.map((job, idx) => (
                <div
                  key={job.id}
                  onClick={() => {
                    setSelectedJob(job);
                    setCurrentView('job-detail');
                  }}
                  className="group bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition cursor-pointer transform hover:-translate-y-2"
                  style={{ animation: 'fadeIn 0.5s ease-out forwards', animationDelay: `${idx * 0.1}s`, opacity: 0 }}
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div className="flex items-start gap-6">
                      <div className="text-5xl">{job.image}</div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-blue-400 transition">
                          {job.title}
                        </h3>
                        <div className="flex flex-wrap gap-4 text-white/70">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            <span>{job.location}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Briefcase className="w-4 h-4" />
                            <span>{job.type}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4" />
                            <span>{job.salary}</span>
                          </div>
                        </div>
                        <p className="text-white/60 mt-3">{job.shortDesc}</p>
                      </div>
                    </div>

                    <button className="flex-shrink-0 bg-gradient-to-r from-blue-600 to-emerald-600 text-white px-8 py-3 rounded-xl font-semibold flex items-center gap-2 group-hover:shadow-lg transition">
                      Apply Now
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-20">
                <p className="text-white/60 text-xl">No positions found matching your criteria. Check back soon!</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}